import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Clock, Video, Check, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOOKAHEAD_DAYS = 21;

// Guest's local IANA tz (sent to the backend + used for all display).
const GUEST_TZ = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
})();

const ymd = (d) => d.toISOString().slice(0, 10);                 // UTC date window bound
const dayKey = (iso) => new Date(iso).toLocaleDateString('en-CA'); // local-tz YYYY-MM-DD bucket
const dayLabel = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
const timeLabel = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

// Batch 6 — funnel end-page booking widget. Resolves the lead's assigned coach
// (prop, else idempotent round-robin), fetches the coach's open slots (Batch 4),
// renders a day/time picker in the GUEST's timezone, and books → Google Calendar
// event + Meet link. Confirmation shows the Meet link; Google emails the invite.
export default function BookingWidget({
    orgId, contactId, coachUserId: coachProp, roundRobinMode,
    brandColor = '#1c1917', buttonColor = '#1c1917',
}) {
    // init | loading | ready | noslots | empty | booking | booked
    const [phase, setPhase] = useState('init');
    const [coachUserId, setCoachUserId] = useState(coachProp || null);
    const [slotsByDay, setSlotsByDay] = useState({});
    const [days, setDays] = useState([]);
    const [selectedDay, setSelectedDay] = useState(null);
    const [selected, setSelected] = useState(null);
    const [booking, setBooking] = useState(null);
    const [errMsg, setErrMsg] = useState('');
    const ranRef = useRef(false);

    const loadSlots = useCallback(async (uid) => {
        setPhase('loading');
        const now = new Date();
        const to = new Date(now.getTime() + LOOKAHEAD_DAYS * 86400000);
        try {
            const res = await axios.get(`${API}/public/coach/${uid}/slots`, {
                params: { org_id: orgId, date_from: ymd(now), date_to: ymd(to) },
            });
            const data = res.data || {};
            if (!data.enabled) { setPhase('noslots'); return; }
            const flat = [];
            Object.values(data.slots_by_day || {}).forEach(arr => (arr || []).forEach(s => flat.push(s)));
            flat.sort((a, b) => String(a.start_utc).localeCompare(String(b.start_utc)));
            const grouped = {};
            flat.forEach(s => { const k = dayKey(s.start_utc); (grouped[k] = grouped[k] || []).push(s); });
            const dk = Object.keys(grouped).sort();
            if (!dk.length) { setPhase('noslots'); return; }
            setSlotsByDay(grouped); setDays(dk);
            setSelectedDay(prev => (prev && grouped[prev]) ? prev : dk[0]);
            setPhase('ready');
        } catch { setPhase('noslots'); }
    }, [orgId]);

    // Resolve coach (prop, else idempotent round-robin assign) → load slots. Once.
    useEffect(() => {
        if (ranRef.current) return; ranRef.current = true;
        let alive = true;
        (async () => {
            let uid = coachProp;
            if (!uid && orgId && contactId) {
                try {
                    const res = await axios.post(`${API}/public/round-robin/assign`, {
                        org_id: orgId, contact_id: contactId, mode: roundRobinMode || undefined,
                    });
                    uid = res.data?.coach_assigned_user_id || null;
                } catch { /* none */ }
            }
            if (!alive) return;
            if (!uid) { setPhase('empty'); return; }   // no coach → render nothing
            setCoachUserId(uid);
            loadSlots(uid);
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const confirm = async () => {
        if (!selected || !coachUserId) return;
        setPhase('booking'); setErrMsg('');
        try {
            const res = await axios.post(`${API}/public/bookings`, {
                org_id: orgId, contact_id: contactId, coach_user_id: coachUserId,
                start_utc: selected.start_utc, timezone: GUEST_TZ,
            });
            setBooking(res.data); setPhase('booked');
        } catch (e) {
            const status = e?.response?.status;
            if (status === 409) {
                setErrMsg(e?.response?.data?.detail || 'That slot was just taken — please pick another.');
                setSelected(null);
                loadSlots(coachUserId);     // refresh availability
            } else {
                setErrMsg('Could not complete the booking. Please try again.');
                setPhase('ready');
            }
        }
    };

    if (phase === 'init' || phase === 'loading') {
        return (
            <div className="flex items-center justify-center gap-2 text-stone-400 text-sm py-8">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading available times…
            </div>
        );
    }
    if (phase === 'empty') return null;   // no coach → nothing (graceful)
    if (phase === 'noslots') {
        return (
            <div className="bg-stone-50 border border-stone-200 rounded-2xl p-6 text-center">
                <Calendar className="w-7 h-7 text-stone-300 mx-auto mb-2" />
                <p className="text-sm text-stone-500">No times available right now — we'll reach out to schedule.</p>
            </div>
        );
    }
    if (phase === 'booked') {
        const b = booking || {};
        return (
            <div className="bg-white border border-stone-200 rounded-2xl p-6 text-center shadow-sm">
                <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: brandColor }}>
                    <Check className="w-6 h-6 text-white" />
                </div>
                <p className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>You're booked!</p>
                <p className="text-sm text-stone-600 mt-1">{dayLabel(b.start_utc)} · {timeLabel(b.start_utc)}</p>
                <p className="text-xs text-stone-400 mt-1">A calendar invite is on its way to your inbox.</p>
                {b.meet_link && (
                    <a href={b.meet_link} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 mt-4 w-full px-5 py-3 rounded-xl text-white font-medium text-sm"
                        style={{ backgroundColor: buttonColor }}>
                        <Video className="w-4 h-4" /> Join Google Meet
                    </a>
                )}
            </div>
        );
    }

    // ready / booking
    const slots = slotsByDay[selectedDay] || [];
    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-5 text-left shadow-sm">
            <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4" style={{ color: brandColor }} />
                <p className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Pick a time</p>
                <span className="text-[11px] text-stone-400 ml-auto">{GUEST_TZ.replace(/_/g, ' ')}</span>
            </div>
            {errMsg && <p className="text-xs text-amber-600 mb-2">{errMsg}</p>}

            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                {days.map(d => {
                    const active = d === selectedDay;
                    const iso = (slotsByDay[d][0] || {}).start_utc;
                    return (
                        <button key={d} type="button" onClick={() => { setSelectedDay(d); setSelected(null); }}
                            className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium border transition ${active ? 'text-white border-transparent' : 'text-stone-600 border-stone-200 hover:border-stone-300'}`}
                            style={active ? { backgroundColor: brandColor } : {}}>
                            {dayLabel(iso)}
                        </button>
                    );
                })}
            </div>

            <div className="grid grid-cols-3 gap-2 mt-3">
                {slots.map(s => {
                    const active = selected && selected.start_utc === s.start_utc;
                    return (
                        <button key={s.start_utc} type="button" onClick={() => setSelected(s)}
                            className={`px-2 py-2 rounded-lg text-xs font-medium border transition ${active ? 'text-white border-transparent' : 'text-stone-700 border-stone-200 hover:border-stone-400'}`}
                            style={active ? { backgroundColor: brandColor } : {}}>
                            {timeLabel(s.start_utc)}
                        </button>
                    );
                })}
            </div>

            {selected && (
                <button type="button" onClick={confirm} disabled={phase === 'booking'}
                    className="mt-4 w-full px-5 py-3 rounded-xl text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ backgroundColor: buttonColor }}>
                    {phase === 'booking'
                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Booking…</>
                        : <><Clock className="w-4 h-4" /> Confirm {dayLabel(selected.start_utc)} · {timeLabel(selected.start_utc)}</>}
                </button>
            )}
        </div>
    );
}
